import { Button, cn, Modal } from '@crm/ui';
import { ChevronLeft, ChevronRight, ImageOff, ZoomIn } from 'lucide-react';
import { useState } from 'react';

interface PhotoPickerProps {
  photos: string[];
  indice: number;
  onChange: (indice: number) => void;
  size?: 'sm' | 'lg' | 'xl';
}

/**
 * Uma foto grande, com setas para andar entre as candidatas - selecionar e
 * so andar ate a que se quer. Sem foto nenhuma, so o icone vazio.
 */
export function PhotoPicker({ photos, indice, onChange, size = 'lg' }: PhotoPickerProps) {
  const foto = photos[indice];

  if (size === 'xl') {
    return <PhotoPickerPeek photos={photos} indice={indice} onChange={onChange} />;
  }

  const lado = size === 'lg' ? 'h-16 w-16' : 'h-11 w-11';

  if (!foto) {
    return (
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground',
          lado,
        )}
      >
        <ImageOff className="h-5 w-5" />
      </span>
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-1">
      {photos.length > 1 ? (
        <button
          type="button"
          aria-label="Foto anterior"
          onClick={() => onChange((indice - 1 + photos.length) % photos.length)}
          className="text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      ) : null}

      <img src={foto} alt="" className={cn('rounded-lg border border-border object-cover', lado)} />

      {photos.length > 1 ? (
        <button
          type="button"
          aria-label="Proxima foto"
          onClick={() => onChange((indice + 1) % photos.length)}
          className="text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Versao "peek" (a partir do sm): a foto atual grande no centro, com metade
 * da anterior e da proxima espiando nas bordas (esmaecendo em direcao aos
 * cantos), e setas grandes por cima - fica claro que da pra folhear. No
 * celular isso nao cabe direito (a foto central some quase toda atras das
 * setas): so a foto principal, largura toda, com Anterior/Proximo embaixo.
 */
function PhotoPickerPeek({ photos, indice, onChange }: Omit<PhotoPickerProps, 'size'>) {
  const total = photos.length;
  const foto = photos[indice];
  const anterior = total > 1 ? photos[(indice - 1 + total) % total] : undefined;
  const proxima = total > 1 ? photos[(indice + 1) % total] : undefined;
  const [zoomAberto, setZoomAberto] = useState(false);

  function voltar() {
    onChange((indice - 1 + total) % total);
  }
  function avancar() {
    onChange((indice + 1) % total);
  }

  return (
    <div className="w-full">
      {/* Celular: foto principal em largura total, altura proporcional. */}
      <div className="space-y-2 sm:hidden">
        {foto ? (
          <button
            type="button"
            onClick={() => setZoomAberto(true)}
            aria-label="Ampliar foto"
            className="group relative block w-full"
          >
            <img
              src={foto}
              alt=""
              className="aspect-[4/3] w-full rounded-md border border-border object-cover shadow-md"
            />
            <span className="absolute inset-0 flex items-center justify-center rounded-md bg-black/0 opacity-0 transition-all group-active:bg-black/30 group-active:opacity-100">
              <ZoomIn className="h-8 w-8 text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]" />
            </span>
          </button>
        ) : (
          <span className="flex aspect-[4/3] w-full items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
            <ImageOff className="h-6 w-6" />
          </span>
        )}

        {total > 1 ? (
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" onClick={voltar}>
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <Button type="button" variant="outline" onClick={avancar}>
              Próximo
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </div>

      {/* Do sm em diante: carrossel "peek", com as vizinhas espiando nas bordas. */}
      <div className="relative hidden h-44 w-full items-center justify-center sm:flex">
        {anterior ? (
          <div className="absolute inset-y-0 left-0 w-[16%] overflow-hidden rounded-l-md">
            <img src={anterior} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-background" />
          </div>
        ) : null}

        {proxima ? (
          <div className="absolute inset-y-0 right-0 w-[16%] overflow-hidden rounded-r-md">
            <img src={proxima} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
          </div>
        ) : null}

        {foto ? (
          <button
            type="button"
            onClick={() => setZoomAberto(true)}
            aria-label="Ampliar foto"
            className="group relative z-10 h-full w-1/2"
          >
            <img
              src={foto}
              alt=""
              className="h-full w-full rounded-md border border-border object-cover shadow-md"
            />
            <span className="absolute inset-0 flex items-center justify-center rounded-md bg-black/0 opacity-0 transition-all group-hover:bg-black/30 group-hover:opacity-100">
              <ZoomIn className="h-8 w-8 text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]" />
            </span>
          </button>
        ) : (
          <span className="relative z-10 flex h-full w-1/2 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
            <ImageOff className="h-6 w-6" />
          </span>
        )}

        {total > 1 ? (
          <>
            <button
              type="button"
              aria-label="Foto anterior"
              onClick={voltar}
              className="-translate-y-1/2 absolute left-1 top-1/2 z-20 text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)] hover:opacity-80"
            >
              <ChevronLeft className="h-14 w-14" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              aria-label="Proxima foto"
              onClick={avancar}
              className="-translate-y-1/2 absolute right-1 top-1/2 z-20 text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)] hover:opacity-80"
            >
              <ChevronRight className="h-14 w-14" strokeWidth={2.5} />
            </button>
          </>
        ) : null}
      </div>

      {foto ? (
        <Modal open={zoomAberto} onClose={() => setZoomAberto(false)} title="Foto" size="large">
          <img
            src={foto}
            alt=""
            className="mx-auto max-h-[70vh] w-auto rounded-md object-contain"
          />
        </Modal>
      ) : null}
    </div>
  );
}
